import apiService from "services/ApiService";
import { throwIfApiConflictOrNotFound } from "./errors";

type HttpMethod = "get" | "post" | "put" | "delete";

interface ApiRequestOptions<TRow = unknown> {
  /** Human-readable label for error messages (e.g. "fetch comp sets") */
  label: string;
  /** Logger instance */
  log: { error: (...args: unknown[]) => void };
  /** Request body */
  data?: unknown;
  /** Pass false to skip cache (default: undefined, let apiService decide) */
  useCache?: boolean;
  /** If set, 409/404 errors throw ConflictError/NotFoundError instead of generic errors */
  conflictLabel?: string;
  /** Row mapper applied to the conflict response row */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API boundary: raw JSON from axios
  mapRow?: (raw: any) => TRow;
}

/**
 * Thin wrapper around `apiService.makeRequest` that standardises
 * error-status checking, logging, and optional 409/404 handling.
 *
 * Returns the raw response so callers can extract what they need
 * (e.g. `response.data`, `response.data.row`, pagination fields…).
 */
export async function apiRequest<TRow = unknown>(
  method: HttpMethod,
  url: string,
  options: ApiRequestOptions<TRow>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  try {
    const response = await apiService.makeRequest(
      method,
      url,
      options.data,
      options.useCache,
    );

    if (response?.status === "error") {
      throw new Error(response.message || `Failed to ${options.label}`);
    }

    return response;
  } catch (error) {
    options.log.error(`Error: ${options.label}:`, error);

    if (options.conflictLabel) {
      throwIfApiConflictOrNotFound(
        error,
        options.conflictLabel,
        options.mapRow,
      );
      // ↑ always throws – line below is only reached when conflictLabel is unset
    }

    throw error;
  }
}
